import React, { useEffect, useState } from 'react';
import '../styles/SignUp.css';
import { Header, Footer, Loading, Input, Button, OrDivider } from '../components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createUser, getDocument, signInWithGoogle, recordEvent, checkOnAuthStateChanged, completeSignUp, getNames } from '../functions';
import { GoogleLogo } from '../assets/icons';

function SignUp() {
	const [signUpWithEmail, setSignUpWithEmail] = useState(false);
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [disabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");;
	const [loading, setLoading] = useState(false);
	const [searchParams] = useSearchParams({});
	const [loggedIn, setLoggedIn] = useState(null);
	const emailParam = searchParams.get("email");
	const navigate = useNavigate();
	const location = useLocation();
	const state = location.state;

	useEffect(() => {
		document.title = "Clozers | Sign Up";

		if ( emailParam !== null ) {
			setEmail(emailParam);
		}

		const fetchUserData = async() => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);
				navigate("/");
            }
            else {
                setLoggedIn(false);
            }
        };
        
        fetchUserData();

	}, [emailParam, navigate]);

	const authenticateEmail = async() => {
		setLoading(true);
		setDisabled(true);
		
		const signUpUser = await createUser(email, password);
		if ( signUpUser.status === 200 ) {
			complete(signUpUser, false);
		}
		else {
			if ( signUpUser.errorCode === "auth/weak-password" ) {
				setErrorMessage("Please use at least 6 characters for your password 🙃");
			}
			else if ( signUpUser.errorCode === "auth/invalid-email" ) {
				setErrorMessage("Please enter a valid email address 👍");
			}
			else if ( signUpUser.errorCode === "auth/email-already-in-use" ) {
				setErrorMessage("Email already in use 😔");
			}
			else if ( signUpUser.errorCode === "auth/network-request-failed" ) {
				setErrorMessage("Connection failed, please try again");
			}
			else {
				alert(signUpUser.message);
			}
			setDisabled(false);
			setLoading(false);
		}
	}

	const googleSignIn = async() => {
		const signUpUser = await signInWithGoogle();
		if ( signUpUser.status === 200 ) {
			setLoading(true);
			setDisabled(true);
			const displayName = signUpUser.user.displayName;
			const displayEmail = signUpUser.user.email;
			const names = getNames(displayName);
			signUpUser.user.firstName = names.firstName;
			signUpUser.user.lastName = names.lastName;
			signUpUser.user.displayName = displayName;
			signUpUser.user.email = displayEmail;
			
			const colRef = "users";
			const userId = signUpUser.user.uid;
			const checkUserDocument = await getDocument(colRef, userId);
			if ( checkUserDocument.status !== 200 ) {
				complete(signUpUser, true);
			}
			else {
				navigate("/", {
					state: state
				});
			}
		}
		else {
			if ( signUpUser.code === "auth/weak-password" ) {
				setErrorMessage("Please use at least 6 characters for your password 🙃");
			}
			else if ( signUpUser.code === "auth/invalid-email" ) {
				setErrorMessage("Please enter a valid email address 👍");
			}
			else if ( signUpUser.code === "auth/email-already-in-use" ) {
				setErrorMessage("Email already in use 😔");
			}
			else if ( signUpUser.code === "auth/network-request-failed" ) {
				setErrorMessage("Connection failed, please try again");
			}
			else if ( signUpUser.code === "auth/popup-closed-by-user" || signUpUser.code === "auth/cancelled-popup-request") {
				console.log("User closed the popup");
			}
			else {
				alert(signUpUser.message);
			}
			setDisabled(false);
			setLoading(false);
		}
	};

	const complete = async(signUpUser, googleAuth) => {
		setErrorMessage("");
		await completeSignUp(signUpUser, googleAuth, email, firstName, lastName);
		navigate("/company-setup", {
			state: state
		});

		setLoading(false);
		setDisabled(false);
	};

	const termsAndConditions = () => {
		navigate("/terms-conditions", {
			state: state
		});
	};

	const loginPage = () => {
		navigate("/login", {
			state: state
		});
	};

	const onChangeFirstName = (text) => {
		setFirstName(text.target.value);
	};

	const onChangeLastName = (text) => {
		setLastName(text.target.value);
	};

	const onChangeEmail = (text) => {
		setEmail(text.target.value);
	};

	const onChangePassword = (text) => {
		setPassword(text.target.value);
	};

	const activateEmailSignup = () => {
		setSignUpWithEmail(true);
		recordEvent("Activate Email Signup", {});
	};

    return (
        <div className="sign-up-outer-container">
            <Header
				loggedIn={loggedIn}
			/>
            <div className="sign-up-inner-container">
                {
                    loading === true ?
                    <Loading />
                    :
                    null
                }
                <div className="login-title-container">
                    <h1 className="heading-large-bold colour-primary text-align-center">
                        Welcome to Clozers!
                    </h1>
                </div>
                {
					signUpWithEmail === false ?
					null
					:
					<div className="sign-up-body-container">
						<div className="sign-up-row">
							<div className="sign-up-element-container sign-up-margin-right">
								<Input
									label={"First name"}
									labelType={"inside"}
									info={false}
									value={firstName}
									onChange={onChangeFirstName}
									placeholder={"Ben"}
									type="text"
									leftUnit={null}
									rightUnit={null}
									unitSwitch={false}
									leftUnitSelected={true}
									unitSwitchFunc={null}
									disabled={false}
									multiline={false}
									error={false}
									onMouseEnter={null}
									onMouseLeave={null}
									reset={false}
									resetFunc={null}
									resetPosition={null}
									focusMultiline={false}
									prompt={false}
								/>
							</div>
							<div className="sign-up-element-container sign-up-margin-left">
								<Input
									label={"Last name"}
									labelType={"inside"}
									info={false}
									value={lastName}
									onChange={onChangeLastName}
									placeholder={"Avery"}
									type="text"
									leftUnit={null}
									rightUnit={null}
									unitSwitch={false}
									leftUnitSelected={true}
									unitSwitchFunc={null}
									disabled={false}
									multiline={false}
									error={false}
									onMouseEnter={null}
									onMouseLeave={null}
									reset={false}
									resetFunc={null}
									resetPosition={null}
									focusMultiline={false}
									prompt={false}
								/>
							</div>
						</div>
						<div className="sign-up-long-element-container">
							<Input
								label={"Email"}
								labelType={"inside"}
								info={false}
								value={email}
								onChange={onChangeEmail}
								placeholder={"youremail@email.com"}
								type="email"
								leftUnit={null}
								rightUnit={null}
								unitSwitch={false}
								leftUnitSelected={true}
								unitSwitchFunc={null}
								disabled={false}
								multiline={false}
								error={false}
								onMouseEnter={null}
								onMouseLeave={null}
								reset={false}
								resetFunc={null}
								resetPosition={null}
								focusMultiline={false}
								prompt={false}
							/>
						</div>
						<div className="sign-up-long-element-container">
							<Input
								label={"Password"}
								labelType={"inside"}
								info={false}
								value={password}
								onChange={onChangePassword}
								placeholder={"••••••••••"}
								type="password"
								leftUnit={null}
								rightUnit={null}
								unitSwitch={false}
								leftUnitSelected={true}
								unitSwitchFunc={null}
								disabled={false}
								multiline={false}
								error={false}
								onMouseEnter={null}
								onMouseLeave={null}
								reset={false}
								resetFunc={null}
								resetPosition={null}
								focusMultiline={false}
								prompt={false}
							/>
							{
								errorMessage !== "" ?
								<div className="sign-up-incorrect-password-container">
									<span className="body-regular colour-error">
										{errorMessage}
									</span>
								</div>
								:
								null
							}
						</div>
					</div>
				}
				<div className="sign-up-bottom-container">
					<span className="body-regular colour-secondary block-text text-align-center">
						By signing up, you agree to our <span onClick={() => termsAndConditions()} className="colour-link underline cursor-pointer">terms of service.</span>
					</span>
					<div className={"sign-up-buttons-outer-container " + (signUpWithEmail === false ? "column-reverse" : "")}>
						<div className="sign-up-button-container">
							<Button
								buttonType="primary-button"
								size="large"
								text={"Sign up"}
								cta={signUpWithEmail === false ? activateEmailSignup : authenticateEmail}
								disabled={signUpWithEmail === false ? false : firstName === "" ? true : lastName === "" ? true : email === "" ? true : password === "" ? true : disabled === true ? true : false}
								leftIcon={null}
								leftIconClass=""
								rightIcon={null}
								rightIconClass=""
								alt=""
								standardIcon={true}
								badge={false}
								badgeVal={null}
								badgeBG={null}
								buttonRef={null}
								spinner={false}
							/>
						</div>
						<OrDivider />
						<div className="sign-up-google-button-container">
							<Button
								buttonType="third-party-button"
								size="large"
								text={"Sign up with Google"}
								cta={googleSignIn}
								disabled={false}
								leftIcon={<GoogleLogo />}
								leftIconClass=""
								rightIcon={null}
								rightIconClass=""
								alt=""
								standardIcon={false}
								badge={false}
								badgeVal={null}
								badgeBG={null}
								buttonRef={null}
								spinner={false}
							/>
						</div>
					</div>
					<div className="sign-up-login-container">
						<span
							className="body-regular colour-primary block-text text-align-center"
						>
							Already have an account? <span onClick={() => loginPage()} className="body-regular-underline colour-link cursor-pointer">Login</span>
						</span>
					</div>
                </div>
            </div>
            <Footer />
        </div>
    )
};

export default SignUp;